import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from '../lib/axios';
import Header from '../components/layout/Header';
import { getMergedContactsCount, purgeMergedContacts } from '../services/apiContacts';
import { useModules } from '../lib/useModules';
import RichTextEditor from '../components/email/RichTextEditor';

export default function Settings() {
    const queryClient = useQueryClient();
    const { isFeatureEnabled } = useModules();
    const [newInstanceName, setNewInstanceName] = useState('');
    const [newProvider, setNewProvider] = useState('evolution');
    const [qrCodeData, setQrCodeData] = useState(null);
    const [desktopToken, setDesktopToken] = useState(null);
    const [message, setMessage] = useState(null);
    const [showPurgeConfirm, setShowPurgeConfirm] = useState(false);
    const [showImapForm, setShowImapForm] = useState(false);
    const [imapForm, setImapForm] = useState({
        email_address: '', display_name: '', visibility: 'shared', private_ai_mode: 'excluded',
        imap_host: '', imap_port: 993, imap_encryption: 'ssl', imap_username: '', imap_password: '',
        smtp_host: '', smtp_port: 587, smtp_encryption: 'tls', smtp_username: '', smtp_password: '',
    });
    const [imapError, setImapError] = useState(null);
    const [connTest, setConnTest] = useState(null);
    // Firma que se está editando: {id, html}. null = ninguna abierta.
    const [editingSignature, setEditingSignature] = useState(null);

    // Parse URL params for OAuth callbacks
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.get('email_connected')) setMessage({ type: 'success', text: 'Cuenta de correo conectada exitosamente.' });
        if (params.get('instagram_connected')) setMessage({ type: 'success', text: 'Cuenta de Instagram conectada exitosamente.' });
        if (params.get('error')) setMessage({ type: 'error', text: `Error en la conexión: ${params.get('error')}` });

        if (params.toString()) {
            window.history.replaceState({}, document.title, window.location.pathname);
        }
    }, []);

    // Fetch instances (WhatsApp)
    const { data: wpInstancesResponse, isLoading: wpLoading } = useQuery({
        queryKey: ['whatsapp-instances'],
        queryFn: () => axios.get('/api/whatsapp/instances'),
    });
    
    const whatsappInstances = wpInstancesResponse?.data?.data || wpInstancesResponse?.data || [];

    // Fetch Email Accounts
    const { data: emailResponse, isLoading: emailLoading } = useQuery({
        queryKey: ['email-accounts'],
        queryFn: () => axios.get('/api/email-accounts'),
    });
    const emailAccounts = emailResponse?.data || [];

    // Fetch Instagram Accounts
    const { data: igResponse, isLoading: igLoading } = useQuery({
        queryKey: ['instagram-accounts'],
        queryFn: () => axios.get('/api/instagram-accounts'),
    });
    const instagramAccounts = igResponse?.data || [];

    // ── WhatsApp Mutations ──
    const connectWpMutation = useMutation({
        mutationFn: ({ instanceName, provider }) => axios.post('/api/whatsapp/instances', { instanceName, provider }),
        onSuccess: (response) => {
            const data = response.data;
            if (data.qrcode) {
                setQrCodeData(data.qrcode);
            }
            if (data.instance_token) {
                setDesktopToken(data.instance_token);
            }
            queryClient.invalidateQueries(['whatsapp-instances']);
        },
        onError: (error) => {
            alert(error.response?.data?.error || 'Error conectando instancia de WhatsApp');
        }
    });

    const toggleWpMutation = useMutation({
        mutationFn: (instanceName) => axios.patch(`/api/whatsapp/instances/${instanceName}/toggle`),
        onSuccess: () => queryClient.invalidateQueries(['whatsapp-instances'])
    });

    const disconnectWpMutation = useMutation({
        mutationFn: (instanceName) => axios.delete(`/api/whatsapp/instances/${instanceName}`),
        onSuccess: () => {
            setDesktopToken(null);
            queryClient.invalidateQueries(['whatsapp-instances']);
        }
    });

    // ── Email/OAuth Mutations ──
    const getOAuthUrlMutation = useMutation({
        mutationFn: (provider) => axios.get(`/api/oauth/${provider}/url`),
        onSuccess: (response) => {
            window.location.href = response.data.url;
        },
        onError: () => alert('No se pudo obtener la URL de autorización.')
    });

    const disconnectEmailMutation = useMutation({
        mutationFn: (id) => axios.delete(`/api/email-accounts/${id}`),
        onSuccess: () => queryClient.invalidateQueries(['email-accounts'])
    });

    const testCredentialsMutation = useMutation({
        mutationFn: (data) => axios.post('/api/email-accounts/test-connection', data),
        onSuccess: (response) => setConnTest(response.data),
        onError: (error) => setConnTest({
            imap: { ok: false, error: error.response?.data?.message || 'No se pudo probar la conexión.' },
            smtp: { ok: false, error: null },
        }),
    });

    const addImapMutation = useMutation({
        mutationFn: (data) => axios.post('/api/email-accounts', data),
        onSuccess: (response) => {
            queryClient.invalidateQueries(['email-accounts']);
            setShowImapForm(false);
            setConnTest(null);
            setImapError(null);
            const conn = response.data?.connection;
            if (conn && !conn.ok) {
                setMessage({ type: 'error', text: `Cuenta guardada, pero la prueba de conexión falló: ${conn.error}` });
            } else {
                setMessage({ type: 'success', text: 'Cuenta de correo agregada y conexión verificada.' });
            }
            setImapForm({
                email_address: '', display_name: '', visibility: 'shared', private_ai_mode: 'excluded',
                imap_host: '', imap_port: 993, imap_encryption: 'ssl', imap_username: '', imap_password: '',
                smtp_host: '', smtp_port: 587, smtp_encryption: 'tls', smtp_username: '', smtp_password: '',
            });
        },
        onError: (error) => {
            setImapError(error.response?.data?.message || 'No se pudo agregar la cuenta. Revisá los datos.');
        }
    });

    const setDefaultEmailMutation = useMutation({
        mutationFn: (id) => axios.post(`/api/email-accounts/${id}/set-default`),
        onSuccess: () => queryClient.invalidateQueries(['email-accounts'])
    });

    const testEmailConnectionMutation = useMutation({
        mutationFn: (id) => axios.post(`/api/email-accounts/${id}/test-connection`),
        onSuccess: (response, id) => {
            queryClient.invalidateQueries(['email-accounts']);
            setMessage(response.data.ok
                ? { type: 'success', text: 'Conexión exitosa.' }
                : { type: 'error', text: `Falló la conexión: ${response.data.error}` });
        }
    });

    const syncEmailMutation = useMutation({
        mutationFn: (id) => axios.post(`/api/email-accounts/${id}/sync`),
        onSuccess: (response) => {
            queryClient.invalidateQueries(['email-accounts']);
            setMessage({ type: 'success', text: `Sincronización completa: ${response.data.synced} correos nuevos.` });
        }
    });

    const updateEmailAccountMutation = useMutation({
        mutationFn: ({ id, data }) => axios.put(`/api/email-accounts/${id}`, data),
        onSuccess: () => queryClient.invalidateQueries(['email-accounts'])
    });

    const disconnectInstagramMutation = useMutation({
        mutationFn: (id) => axios.delete(`/api/instagram-accounts/${id}`),
        onSuccess: () => queryClient.invalidateQueries(['instagram-accounts'])
    });

    // ── Merged Contacts ──
    const { data: mergedCountData, refetch: refetchMergedCount } = useQuery({
        queryKey: ['merged-contacts-count'],
        queryFn: getMergedContactsCount,
    });
    const mergedCount = mergedCountData?.count ?? 0;

    const purgeMutation = useMutation({
        mutationFn: purgeMergedContacts,
        onSuccess: (data) => {
            setShowPurgeConfirm(false);
            setMessage({ type: 'success', text: data.message });
            refetchMergedCount();
        },
        onError: (err) => {
            setShowPurgeConfirm(false);
            setMessage({ type: 'error', text: err.response?.data?.message || 'Error al purgar.' });
        },
    });

    const handleConnectWhatsApp = () => {
        if (!newInstanceName.trim()) return;
        connectWpMutation.mutate({ instanceName: newInstanceName, provider: newProvider });
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        setMessage({ type: 'success', text: 'Token copiado al portapapeles.' });
        setTimeout(() => setMessage(null), 3000);
    };

    return (
        <div className="flex flex-col h-full bg-slate-50 dark:bg-background-dark">
            <Header title="Configuración" />
            
            <div className="flex-1 overflow-y-auto p-4 md:p-8 no-scrollbar">
                <div className="max-w-4xl mx-auto space-y-8">
                    
                    {message && (
                        <div className={`p-4 rounded-xl border ${message.type === 'error' ? 'bg-red-50 text-red-700 border-red-200' : 'bg-green-50 text-green-700 border-green-200'} flex justify-between items-center`}>
                            <span>{message.text}</span>
                            <button onClick={() => setMessage(null)} className="text-xl font-bold">&times;</button>
                        </div>
                    )}

                    {/* General Settings */}
                    <section className="bg-white dark:bg-surface-dark rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm overflow-hidden animate-in fade-in duration-500">
                        <div className="p-6 border-b border-gray-100 dark:border-gray-800">
                            <h3 className="text-lg font-bold text-text-main dark:text-white">Perfil</h3>
                            <p className="text-sm text-text-secondary">Información de tu cuenta y preferencias personales.</p>
                        </div>
                        <div className="p-6 space-y-6">
                            <div className="flex items-center gap-6">
                                <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center text-primary text-2xl font-bold">
                                    <span className="material-symbols-outlined text-4xl">person</span>
                                </div>
                                <div>
                                    <button className="bg-primary px-4 py-2 text-white text-sm font-bold rounded-lg hover:bg-primary/90 transition-colors shadow-sm mb-2">
                                        Cambiar Foto
                                    </button>
                                    <p className="text-xs text-text-secondary">JPG, GIF o PNG. Máx 800K.</p>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* General Settings (Timezone, etc.) */}
                    {isFeatureEnabled('settings', 'general') && (
                        <section className="bg-gradient-to-br from-orange-500 to-amber-600 dark:from-orange-600 dark:to-amber-800 rounded-2xl shadow-xl overflow-hidden animate-in fade-in duration-500 group cursor-pointer" onClick={() => window.location.href='/settings/general'}>
                            <div className="p-6 flex items-center justify-between">
                                <div className="flex items-center gap-4">
                                    <div className="w-14 h-14 rounded-xl bg-white/20 backdrop-blur-md flex items-center justify-center text-white">
                                        <span className="material-symbols-outlined text-3xl">tune</span>
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-bold text-white tracking-tight">Configuración General</h3>
                                        <p className="text-orange-100 text-sm">Define la zona horaria de tu negocio y otros ajustes generales.</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 text-white/50 group-hover:text-white transition-colors">
                                    <span className="text-xs font-bold uppercase tracking-widest bg-white/10 px-2 py-1 rounded">Configurar</span>
                                    <span className="material-symbols-outlined group-hover:translate-x-1 transition-transform">arrow_forward</span>
                                </div>
                            </div>
                        </section>
                    )}

                    {/* AI Strategy Settings Link */}
                    {isFeatureEnabled('settings', 'ai') && (
                        <section className="bg-gradient-to-br from-blue-600 to-indigo-700 dark:from-blue-700 dark:to-indigo-900 rounded-2xl shadow-xl overflow-hidden animate-in fade-in duration-500 group cursor-pointer" onClick={() => window.location.href='/settings/ai'}>
                            <div className="p-6 flex items-center justify-between">
                                <div className="flex items-center gap-4">
                                    <div className="w-14 h-14 rounded-xl bg-white/20 backdrop-blur-md flex items-center justify-center text-white">
                                        <span className="material-symbols-outlined text-3xl">psychology</span>
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-bold text-white tracking-tight">Inteligencia Artificial</h3>
                                        <p className="text-blue-100 text-sm">Configura el Piloto Automático, prompts y modelos de IA.</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 text-white/50 group-hover:text-white transition-colors">
                                    <span className="text-xs font-bold uppercase tracking-widest bg-white/10 px-2 py-1 rounded">Configurar</span>
                                    <span className="material-symbols-outlined group-hover:translate-x-1 transition-transform">arrow_forward</span>
                                </div>
                            </div>
                        </section>
                    )}

                    {/* BCV Exchange Rate Card */}
                    {isFeatureEnabled('settings', 'bcv') && (
                        <section className="bg-gradient-to-br from-emerald-600 to-teal-700 dark:from-emerald-700 dark:to-teal-900 rounded-2xl shadow-xl overflow-hidden animate-in fade-in duration-500 group cursor-pointer" onClick={() => window.location.href='/settings/bcv'}>
                            <div className="p-6 flex items-center justify-between">
                                <div className="flex items-center gap-4">
                                    <div className="w-14 h-14 rounded-xl bg-white/20 backdrop-blur-md flex items-center justify-center text-white">
                                        <span className="material-symbols-outlined text-3xl">payments</span>
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-bold text-white tracking-tight">Tasa Cambiaria (BCV)</h3>
                                        <p className="text-emerald-100 text-sm">Visualiza la tasa oficial del BCV, histórico y gráficos.</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 text-white/50 group-hover:text-white transition-colors">
                                    <span className="text-xs font-bold uppercase tracking-widest bg-white/10 px-2 py-1 rounded">Ver tasa</span>
                                    <span className="material-symbols-outlined group-hover:translate-x-1 transition-transform">arrow_forward</span>
                                </div>
                            </div>
                        </section>
                    )}

                    {/* Notificaciones: preferencias personales, sin gate de modulo */}
                    <section className="bg-gradient-to-br from-violet-600 to-purple-700 dark:from-violet-700 dark:to-purple-900 rounded-2xl shadow-xl overflow-hidden animate-in fade-in duration-500 group cursor-pointer" onClick={() => window.location.href='/settings/notifications'}>
                        <div className="p-6 flex items-center justify-between">
                            <div className="flex items-center gap-4">
                                <div className="w-14 h-14 rounded-xl bg-white/20 backdrop-blur-md flex items-center justify-center text-white">
                                    <span className="material-symbols-outlined text-3xl">notifications_active</span>
                                </div>
                                <div>
                                    <h3 className="text-xl font-bold text-white tracking-tight">Notificaciones</h3>
                                    <p className="text-violet-100 text-sm">Elegi que avisos queres recibir. Es tu configuracion personal.</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2 text-white/50 group-hover:text-white transition-colors">
                                <span className="text-xs font-bold uppercase tracking-widest bg-white/10 px-2 py-1 rounded">Configurar</span>
                                <span className="material-symbols-outlined group-hover:translate-x-1 transition-transform">arrow_forward</span>
                            </div>
                        </div>
                    </section>

                    {/* App movil: sin gate de modulo a proposito — el vendedor
                        que mas necesita el telefono es justamente el que no
                        tiene permisos de configuracion. Es un <a> y no un
                        onClick con window.location porque /movil/ es una
                        carpeta estatica fuera del SPA: se abre en otra pestaña
                        para no cerrarle el CRM a nadie. */}
                    <section className="bg-gradient-to-br from-emerald-600 to-green-700 dark:from-emerald-700 dark:to-green-900 rounded-2xl shadow-xl overflow-hidden animate-in fade-in duration-500 group">
                        <a href="/movil/" target="_blank" rel="noopener noreferrer" className="flex p-6 items-center justify-between gap-4">
                            <div className="flex items-center gap-4">
                                <div className="w-14 h-14 rounded-xl bg-white/20 backdrop-blur-md flex items-center justify-center text-white">
                                    <span className="material-symbols-outlined text-3xl">phone_android</span>
                                </div>
                                <div>
                                    <h3 className="text-xl font-bold text-white tracking-tight">App para Android</h3>
                                    <p className="text-emerald-100 text-sm">Chat, Tutor IA, Leads y Contactos en tu telefono. Android 7+.</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2 text-white/50 group-hover:text-white transition-colors shrink-0">
                                <span className="text-xs font-bold uppercase tracking-widest bg-white/10 px-2 py-1 rounded">Descargar</span>
                                <span className="material-symbols-outlined group-hover:translate-y-0.5 transition-transform">download</span>
                            </div>
                        </a>
                    </section>

                    {/* Campos personalizados */}
                    {isFeatureEnabled('contacts', 'custom_fields') && (
                    <section className="bg-gradient-to-br from-teal-600 to-cyan-700 dark:from-teal-700 dark:to-cyan-900 rounded-2xl shadow-xl overflow-hidden animate-in fade-in duration-500 group cursor-pointer" onClick={() => window.location.href='/settings/custom-fields'}>
                        <div className="p-6 flex items-center justify-between">
                            <div className="flex items-center gap-4">
                                <div className="w-14 h-14 rounded-xl bg-white/20 backdrop-blur-md flex items-center justify-center text-white">
                                    <span className="material-symbols-outlined text-3xl">tune</span>
                                </div>
                                <div>
                                    <h3 className="text-xl font-bold text-white tracking-tight">Campos Personalizados</h3>
                                    <p className="text-teal-100 text-sm">Datos propios de tu operacion en la ficha de cada contacto.</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2 text-white/50 group-hover:text-white transition-colors">
                                <span className="text-xs font-bold uppercase tracking-widest bg-white/10 px-2 py-1 rounded">Gestionar</span>
                                <span className="material-symbols-outlined group-hover:translate-x-1 transition-transform">arrow_forward</span>
                            </div>
                        </div>
                    </section>
                    )}

                    {/* WhatsApp Integration */}
                    {isFeatureEnabled('settings', 'whatsapp') && (
                    <section className="bg-white dark:bg-surface-dark rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm overflow-hidden animate-in fade-in duration-500">
                        <div className="p-6 border-b border-gray-100 dark:border-gray-800 flex justify-between items-center bg-green-50/10 dark:bg-green-900/10">
                            <div>
                                <h3 className="text-lg font-bold text-text-main dark:text-white flex items-center gap-2">
                                    <span className="material-symbols-outlined text-green-500">whatsapp</span>
                                    WhatsApp
                                </h3>
                                <p className="text-sm text-text-secondary">Conecta instancias de WhatsApp al CRM.</p>
                            </div>
                            <div className="flex gap-2">
                                <span className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 text-xs px-2.5 py-1 rounded-full font-bold">Evolution API</span>
                                <span className="bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400 text-xs px-2.5 py-1 rounded-full font-bold">Desktop</span>
                            </div>
                        </div>
                        
                        <div className="p-6 space-y-6">
                            {/* Provider selector */}
                            <div className="space-y-3">
                                <label className="text-sm font-semibold text-text-main dark:text-gray-200">Tipo de conexión</label>
                                <div className="grid grid-cols-2 gap-3">
                                    <button
                                        type="button"
                                        onClick={() => setNewProvider('evolution')}
                                        className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all ${newProvider === 'evolution' ? 'border-green-500 bg-green-50 dark:bg-green-900/20' : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'}`}
                                    >
                                        <span className="material-symbols-outlined text-green-500 text-2xl">cloud</span>
                                        <span className="text-xs font-bold dark:text-white">Cloud (Evolution)</span>
                                        <span className="text-[10px] text-text-secondary text-center">Requiere Evolution API activa</span>
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setNewProvider('desktop')}
                                        className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all ${newProvider === 'desktop' ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20' : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'}`}
                                    >
                                        <span className="material-symbols-outlined text-indigo-500 text-2xl">computer</span>
                                        <span className="text-xs font-bold dark:text-white">Local (Desktop)</span>
                                        <span className="text-[10px] text-text-secondary text-center">Sin puertos · Sin IP fija</span>
                                    </button>
                                </div>
                            </div>

                            {/* Instance name + connect button */}
                            <div className="flex items-end gap-3">
                                <div className="flex-1 space-y-2">
                                    <label className="text-sm font-semibold text-text-main dark:text-gray-200">Nombre de instancia</label>
                                    <input 
                                        type="text" 
                                        value={newInstanceName}
                                        onChange={(e) => setNewInstanceName(e.target.value.replace(/\s+/g, '-').toLowerCase())}
                                        className="w-full bg-white dark:bg-background-dark border border-gray-200 dark:border-gray-700 rounded-lg p-2.5 text-sm" 
                                        placeholder="ej. ventas-principal" 
                                    />
                                </div>
                                <button 
                                    onClick={handleConnectWhatsApp}
                                    disabled={connectWpMutation.isPending || !newInstanceName.trim()}
                                    className="bg-primary px-6 py-2.5 text-white text-sm font-bold rounded-lg hover:bg-primary/90 transition-colors shadow-sm disabled:opacity-50 h-[42px]"
                                >
                                    {newProvider === 'desktop' ? 'Generar Token' : 'Conectar QR'}
                                </button>
                            </div>

                            {/* Desktop token display */}
                            {desktopToken && (
                                <div className="p-5 bg-indigo-50 dark:bg-indigo-900/20 rounded-xl border border-indigo-200 dark:border-indigo-800 animate-in zoom-in-95">
                                    <div className="flex items-center gap-2 mb-3">
                                        <span className="material-symbols-outlined text-indigo-500">vpn_key</span>
                                        <h4 className="font-bold text-indigo-800 dark:text-indigo-300">Token de Enlace Generado</h4>
                                    </div>
                                    <div className="flex items-center gap-2 bg-white dark:bg-background-dark rounded-lg p-3 border border-indigo-200 dark:border-indigo-700 mb-3">
                                        <code className="flex-1 text-sm font-mono text-indigo-700 dark:text-indigo-300 break-all">{desktopToken}</code>
                                        <button onClick={() => copyToClipboard(desktopToken)} className="p-1.5 hover:bg-indigo-100 dark:hover:bg-indigo-900/40 rounded-md transition-colors">
                                            <span className="material-symbols-outlined text-indigo-500 text-base">content_copy</span>
                                        </button>
                                    </div>
                                    <ol className="text-xs text-indigo-700 dark:text-indigo-300 space-y-1 list-decimal list-inside">
                                        <li>Descarga e instala <strong>Tornado Desktop</strong>.</li>
                                        <li>Abre la app y pega este token.</li>
                                        <li>Escanea el QR con tu teléfono.</li>
                                    </ol>
                                    <button className="mt-3 w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-bold transition-colors">
                                        <span className="material-symbols-outlined text-base">download</span>
                                        Descargar Tornado Desktop (.exe)
                                    </button>
                                </div>
                            )}

                            {/* QR Code for Evolution */}
                            {qrCodeData && (
                                <div className="p-6 bg-slate-900 rounded-xl flex flex-col items-center justify-center text-center animate-in zoom-in-95">
                                    <h4 className="text-white font-bold mb-2">Escanea este código con WhatsApp</h4>
                                    <div className="bg-white p-4 rounded-xl mb-4">
                                        <img src={qrCodeData.startsWith('data:image') || qrCodeData.startsWith('http') ? qrCodeData : `data:image/png;base64,${qrCodeData}`} alt="QR Code" className="w-[200px] h-[200px]" />
                                    </div>
                                    <button 
                                        onClick={() => { setQrCodeData(null); queryClient.invalidateQueries(['whatsapp-instances']); }}
                                        className="text-sm text-gray-400 hover:text-white"
                                    >Cerrar</button>
                                </div>
                            )}

                            {/* Instance list */}
                            <div className="space-y-3">
                                <h4 className="text-sm font-bold text-gray-800 dark:text-gray-200 border-b border-gray-100 dark:border-gray-800 pb-2">Instancias Conectadas</h4>
                                {wpLoading ? (
                                    <div className="text-sm text-gray-500">Cargando...</div>
                                ) : (!Array.isArray(whatsappInstances) || whatsappInstances.length === 0) ? (
                                    <div className="text-sm text-gray-400">No hay instancias conectadas.</div>
                                ) : (
                                    (whatsappInstances || []).map((instance, idx) => {
                                        if (typeof instance === 'string') return null;
                                        const name = instance?.instance_name || instance?.instance?.instanceName || instance?.name || instance?.instanceName;
                                        const provider = instance?.provider || 'evolution';
                                        const isActive = instance?.is_active !== false;
                                        const status = instance?.connection_status || instance?.evolution_status || instance?.instance?.status || 'unknown';
                                        const token = instance?.instance_token;

                                        return (
                                            <div key={idx} className={`flex flex-col gap-3 p-4 bg-white dark:bg-slate-800/20 border rounded-xl transition-opacity ${!isActive ? 'opacity-50' : ''} ${provider === 'desktop' ? 'border-indigo-200 dark:border-indigo-800/50' : 'border-gray-200 dark:border-gray-700/50'}`}>
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center gap-3">
                                                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${provider === 'desktop' ? 'bg-indigo-100 dark:bg-indigo-900/30' : 'bg-green-100 dark:bg-green-900/30'}`}>
                                                            <span className={`material-symbols-outlined ${provider === 'desktop' ? 'text-indigo-600' : 'text-green-600'}`}>
                                                                {provider === 'desktop' ? 'computer' : 'smartphone'}
                                                            </span>
                                                        </div>
                                                        <div>
                                                            <p className="text-sm font-bold dark:text-white">{name}</p>
                                                            <div className="flex items-center gap-2">
                                                                <span className={`text-xs px-1.5 py-0.5 rounded font-bold ${
                                                                    provider === 'desktop'
                                                                        ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400'
                                                                        : 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                                                }`}>{provider === 'desktop' ? 'Desktop' : 'Cloud'}</span>
                                                                <span className={`text-xs ${
                                                                    status === 'open' || status === 'online' ? 'text-green-500' :
                                                                    status === 'connecting' ? 'text-amber-500' : 'text-gray-400'
                                                                }`}>
                                                                    {status === 'open' || status === 'online' ? 'Conectado' :
                                                                     status === 'connecting' ? 'Conectando...' :
                                                                     status === 'awaiting_qr' ? 'Esperando QR' :
                                                                     status === 'paused' ? 'En pausa' : 'Desconectado'}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        {/* Pause/Resume toggle */}
                                                        <button
                                                            onClick={() => toggleWpMutation.mutate(name)}
                                                            disabled={toggleWpMutation.isPending}
                                                            title={isActive ? 'Pausar instancia' : 'Reanudar instancia'}
                                                            className={`p-2 rounded-lg transition-colors ${
                                                                isActive
                                                                    ? 'hover:bg-amber-50 text-amber-500 dark:hover:bg-amber-900/20'
                                                                    : 'hover:bg-green-50 text-green-500 dark:hover:bg-green-900/20'
                                                            }`}
                                                        >
                                                            <span className="material-symbols-outlined">{isActive ? 'pause_circle' : 'play_circle'}</span>
                                                        </button>
                                                        {/* Disconnect */}
                                                        <button onClick={() => {
                                                            if (window.confirm('¿Desconectar y eliminar esta instancia?')) disconnectWpMutation.mutate(name);
                                                        }} className="p-2 hover:bg-red-50 text-red-500 rounded-lg">
                                                            <span className="material-symbols-outlined">link_off</span>
                                                        </button>
                                                    </div>
                                                </div>

                                                {/* Show token for Desktop instances */}
                                                {provider === 'desktop' && token && (
                                                    <div className="flex items-center gap-2 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg px-3 py-2">
                                                        <span className="material-symbols-outlined text-indigo-400 text-sm">vpn_key</span>
                                                        <code className="text-xs font-mono text-indigo-700 dark:text-indigo-300 flex-1 truncate">{token}</code>
                                                        <button onClick={() => copyToClipboard(token)} className="flex-shrink-0 p-1 hover:bg-indigo-100 rounded">
                                                            <span className="material-symbols-outlined text-indigo-400 text-sm">content_copy</span>
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                        </div>
                    </section>
                    )}

                    {/* Email Integrations */}
                    {isFeatureEnabled('settings', 'email') && (
                    <section className="bg-white dark:bg-surface-dark rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm overflow-hidden animate-in fade-in duration-500">
                        <div className="p-6 border-b border-gray-100 dark:border-gray-800 flex justify-between items-center bg-blue-50/50 dark:bg-blue-900/10">
                            <div>
                                <h3 className="text-lg font-bold text-text-main dark:text-white flex items-center gap-2">
                                    <span className="material-symbols-outlined text-blue-500">mail</span>
                                    Cuentas de Correo
                                </h3>
                                <p className="text-sm text-text-secondary">Vincula tu Gmail u Outlook para sincronizar correos con contactos.</p>
                            </div>
                        </div>
                        
                        <div className="p-6 space-y-6">
                            <div className="flex gap-4">
                                <button 
                                    onClick={() => getOAuthUrlMutation.mutate('google')}
                                    disabled={getOAuthUrlMutation.isPending}
                                    className="flex-1 flex items-center justify-center gap-3 py-3 px-4 border border-gray-200 dark:border-gray-700 rounded-xl hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors"
                                >
                                    <img src="https://www.svgrepo.com/show/475656/google-color.svg" alt="Google" className="w-6 h-6" />
                                    <span className="text-sm font-bold dark:text-white">Conectar Gmail</span>
                                </button>
                                <button 
                                    onClick={() => getOAuthUrlMutation.mutate('microsoft')}
                                    disabled={getOAuthUrlMutation.isPending}
                                    className="flex-1 flex items-center justify-center gap-3 py-3 px-4 border border-gray-200 dark:border-gray-700 rounded-xl hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors"
                                >
                                    <img src="https://www.svgrepo.com/show/475668/microsoft-color.svg" alt="Microsoft" className="w-6 h-6" />
                                    <span className="text-sm font-bold dark:text-white">Conectar Outlook</span>
                                </button>
                            </div>

                            <div>
                                <button
                                    onClick={() => { setConnTest(null); setImapError(null); setShowImapForm((v) => !v); }}
                                    className="w-full sm:w-auto flex items-center justify-center gap-2 py-3 px-4 border border-dashed border-gray-300 dark:border-gray-600 rounded-xl hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors text-sm font-bold dark:text-white"
                                >
                                    <span className="material-symbols-outlined text-base">alternate_email</span>
                                    {showImapForm ? 'Cancelar' : 'Agregar cuenta IMAP/SMTP'}
                                </button>

                                {showImapForm && (
                                    <form
                                        onSubmit={(e) => { e.preventDefault(); setImapError(null); addImapMutation.mutate(imapForm); }}
                                        className="mt-4 p-4 border border-gray-200 dark:border-gray-700 rounded-xl space-y-3 bg-gray-50/50 dark:bg-slate-900/30"
                                    >
                                        {imapError && <div className="text-sm text-red-500 bg-red-50 dark:bg-red-900/20 p-2 rounded-lg">{imapError}</div>}
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                            <input required type="email" placeholder="Dirección de correo" value={imapForm.email_address}
                                                onChange={(e) => setImapForm({ ...imapForm, email_address: e.target.value })}
                                                className="text-sm p-2 border border-gray-200 dark:border-gray-700 rounded-lg dark:bg-slate-800 dark:text-white" />
                                            <input type="text" placeholder="Nombre a mostrar (opcional)" value={imapForm.display_name}
                                                onChange={(e) => setImapForm({ ...imapForm, display_name: e.target.value })}
                                                className="text-sm p-2 border border-gray-200 dark:border-gray-700 rounded-lg dark:bg-slate-800 dark:text-white" />
                                        </div>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                            <select value={imapForm.visibility} onChange={(e) => setImapForm({ ...imapForm, visibility: e.target.value })}
                                                className="text-sm p-2 border border-gray-200 dark:border-gray-700 rounded-lg dark:bg-slate-800 dark:text-white">
                                                <option value="shared">Compartida con el equipo</option>
                                                <option value="private">Privada (sólo yo)</option>
                                            </select>
                                            {imapForm.visibility === 'private' && (
                                                <select value={imapForm.private_ai_mode} onChange={(e) => setImapForm({ ...imapForm, private_ai_mode: e.target.value })}
                                                    className="text-sm p-2 border border-gray-200 dark:border-gray-700 rounded-lg dark:bg-slate-800 dark:text-white">
                                                    <option value="excluded">IA: no leer estos correos</option>
                                                    <option value="generalized">IA: leer pero generalizar (no citar detalles)</option>
                                                </select>
                                            )}
                                        </div>

                                        <p className="text-xs font-bold text-gray-500 pt-2">IMAP (recepción)</p>
                                        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                                            <input required type="text" placeholder="Host" value={imapForm.imap_host}
                                                onChange={(e) => setImapForm({ ...imapForm, imap_host: e.target.value })}
                                                className="text-sm p-2 border border-gray-200 dark:border-gray-700 rounded-lg dark:bg-slate-800 dark:text-white sm:col-span-2" />
                                            <input required type="number" placeholder="Puerto" value={imapForm.imap_port}
                                                onChange={(e) => setImapForm({ ...imapForm, imap_port: Number(e.target.value) })}
                                                className="text-sm p-2 border border-gray-200 dark:border-gray-700 rounded-lg dark:bg-slate-800 dark:text-white" />
                                            <select value={imapForm.imap_encryption} onChange={(e) => setImapForm({ ...imapForm, imap_encryption: e.target.value })}
                                                className="text-sm p-2 border border-gray-200 dark:border-gray-700 rounded-lg dark:bg-slate-800 dark:text-white">
                                                <option value="ssl">SSL</option>
                                                <option value="tls">TLS</option>
                                                <option value="none">Ninguno</option>
                                            </select>
                                        </div>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                            <input type="text" placeholder="Usuario IMAP (por defecto, el email)" value={imapForm.imap_username}
                                                onChange={(e) => setImapForm({ ...imapForm, imap_username: e.target.value })}
                                                className="text-sm p-2 border border-gray-200 dark:border-gray-700 rounded-lg dark:bg-slate-800 dark:text-white" />
                                            <input required type="password" placeholder="Contraseña IMAP" value={imapForm.imap_password}
                                                onChange={(e) => setImapForm({ ...imapForm, imap_password: e.target.value })}
                                                className="text-sm p-2 border border-gray-200 dark:border-gray-700 rounded-lg dark:bg-slate-800 dark:text-white" />
                                        </div>

                                        <p className="text-xs font-bold text-gray-500 pt-2">SMTP (envío)</p>
                                        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                                            <input type="text" placeholder="Host" value={imapForm.smtp_host}
                                                onChange={(e) => setImapForm({ ...imapForm, smtp_host: e.target.value })}
                                                className="text-sm p-2 border border-gray-200 dark:border-gray-700 rounded-lg dark:bg-slate-800 dark:text-white sm:col-span-2" />
                                            <input type="number" placeholder="Puerto" value={imapForm.smtp_port}
                                                onChange={(e) => setImapForm({ ...imapForm, smtp_port: Number(e.target.value) })}
                                                className="text-sm p-2 border border-gray-200 dark:border-gray-700 rounded-lg dark:bg-slate-800 dark:text-white" />
                                            <select value={imapForm.smtp_encryption} onChange={(e) => setImapForm({ ...imapForm, smtp_encryption: e.target.value })}
                                                className="text-sm p-2 border border-gray-200 dark:border-gray-700 rounded-lg dark:bg-slate-800 dark:text-white">
                                                <option value="tls">TLS</option>
                                                <option value="ssl">SSL</option>
                                                <option value="none">Ninguno</option>
                                            </select>
                                        </div>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                            <input type="text" placeholder="Usuario SMTP (por defecto, el mismo que IMAP)" value={imapForm.smtp_username}
                                                onChange={(e) => setImapForm({ ...imapForm, smtp_username: e.target.value })}
                                                className="text-sm p-2 border border-gray-200 dark:border-gray-700 rounded-lg dark:bg-slate-800 dark:text-white" />
                                            <input type="password" placeholder="Contraseña SMTP (por defecto, la misma que IMAP)" value={imapForm.smtp_password}
                                                onChange={(e) => setImapForm({ ...imapForm, smtp_password: e.target.value })}
                                                className="text-sm p-2 border border-gray-200 dark:border-gray-700 rounded-lg dark:bg-slate-800 dark:text-white" />
                                        </div>

                                        {connTest && (
                                            <div className="space-y-1.5 p-3 rounded-xl bg-gray-50 dark:bg-slate-900/40 border border-gray-200 dark:border-gray-700">
                                                {['imap', 'smtp'].map((proto) => {
                                                    const r = connTest[proto];
                                                    if (!r) return null;
                                                    const tone = r.ok ? 'text-emerald-600' : (r.skipped ? 'text-amber-600' : 'text-red-500');
                                                    const icon = r.ok ? 'check_circle' : (r.skipped ? 'info' : 'cancel');
                                                    return (
                                                        <div key={proto} className={`text-xs flex items-start gap-1.5 ${tone}`}>
                                                            <span className="material-symbols-outlined text-sm shrink-0">{icon}</span>
                                                            <span>
                                                                <b>{proto.toUpperCase()}:</b>{' '}
                                                                {r.ok ? 'conexión correcta' : (r.error || 'falló la conexión')}
                                                            </span>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}

                                        <div className="flex flex-col sm:flex-row gap-2">
                                            <button type="submit" disabled={addImapMutation.isPending || testCredentialsMutation.isPending}
                                                className="py-2 px-6 bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold rounded-xl transition-colors disabled:opacity-50">
                                                {addImapMutation.isPending ? 'Guardando y probando conexión...' : 'Guardar cuenta'}
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => { setConnTest(null); testCredentialsMutation.mutate(imapForm); }}
                                                disabled={
                                                    testCredentialsMutation.isPending || addImapMutation.isPending ||
                                                    !imapForm.email_address || !imapForm.imap_host || !imapForm.imap_password
                                                }
                                                className="py-2 px-6 border border-gray-200 dark:border-gray-700 text-sm font-bold rounded-xl transition-colors disabled:opacity-50 flex items-center justify-center gap-2 dark:text-white"
                                                title="Comprueba las credenciales sin guardar la cuenta"
                                            >
                                                <span className={`material-symbols-outlined text-lg ${testCredentialsMutation.isPending ? 'animate-spin' : ''}`}>
                                                    {testCredentialsMutation.isPending ? 'progress_activity' : 'network_check'}
                                                </span>
                                                {testCredentialsMutation.isPending ? 'Probando...' : 'Probar conexión'}
                                            </button>
                                        </div>
                                    </form>
                                )}
                            </div>

                            <div className="space-y-3">
                                <h4 className="text-sm font-bold text-gray-800 dark:text-gray-200 border-b border-gray-100 dark:border-gray-800 pb-2">Cuentas Conectadas</h4>
                                {emailLoading ? (
                                    <div className="text-sm text-gray-500">Cargando...</div>
                                ) : emailAccounts.length === 0 ? (
                                    <div className="text-sm text-gray-400">No tienes cuentas de correo conectadas.</div>
                                ) : (
                                    (emailAccounts || []).map((acc) => (
                                        <div key={acc.id} className="flex flex-col gap-3 p-4 bg-white dark:bg-slate-800/20 border border-gray-200 dark:border-gray-700/50 rounded-xl">
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-4">
                                                    <div className="w-10 h-10 rounded-full bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center">
                                                        <span className="material-symbols-outlined text-blue-500">
                                                            {acc.provider === 'google' ? 'mark_email_unread' : 'mail'}
                                                        </span>
                                                    </div>
                                                    <div>
                                                        <p className="text-sm font-bold dark:text-white flex items-center gap-2">
                                                            {acc.display_name || acc.email_address}
                                                            {acc.is_default && <span className="text-[10px] font-bold uppercase text-blue-600 bg-blue-50 dark:bg-blue-900/30 px-2 py-0.5 rounded-full">Predeterminada</span>}
                                                        </p>
                                                        <p className="text-xs text-gray-500">
                                                            {acc.email_address} · <span className="capitalize">{acc.provider}</span> · {acc.visibility === 'private' ? 'Privada' : 'Compartida'}
                                                        </p>
                                                        {acc.last_sync_error && (
                                                            <p className="text-xs text-red-500 mt-1">⚠ {acc.last_sync_error}</p>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-1">
                                                    <button title="Sincronizar ahora" onClick={() => syncEmailMutation.mutate(acc.id)}
                                                        disabled={syncEmailMutation.isPending}
                                                        className="p-2 hover:bg-blue-50 dark:hover:bg-blue-900/20 text-blue-500 rounded-lg">
                                                        <span className="material-symbols-outlined text-lg">sync</span>
                                                    </button>
                                                    <button title="Probar conexión" onClick={() => testEmailConnectionMutation.mutate(acc.id)}
                                                        disabled={testEmailConnectionMutation.isPending}
                                                        className="p-2 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 text-emerald-500 rounded-lg">
                                                        <span className="material-symbols-outlined text-lg">network_check</span>
                                                    </button>
                                                    <button onClick={() => {
                                                        if(window.confirm('¿Desconectar esta cuenta?')) disconnectEmailMutation.mutate(acc.id);
                                                    }} className="p-2 hover:bg-red-50 dark:hover:bg-red-900/20 text-red-500 rounded-lg">
                                                        <span className="material-symbols-outlined text-lg">link_off</span>
                                                    </button>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-4 pl-14 flex-wrap">
                                                {!acc.is_default && (
                                                    <button onClick={() => setDefaultEmailMutation.mutate(acc.id)} className="text-xs font-bold text-blue-600 hover:underline">
                                                        Marcar como predeterminada
                                                    </button>
                                                )}
                                                <label className="text-xs flex items-center gap-1 text-gray-500">
                                                    <input type="checkbox" checked={acc.visibility === 'private'}
                                                        onChange={(e) => updateEmailAccountMutation.mutate({ id: acc.id, data: { visibility: e.target.checked ? 'private' : 'shared' } })} />
                                                    Privada
                                                </label>
                                                {acc.visibility === 'private' && (
                                                    <select value={acc.private_ai_mode || 'excluded'}
                                                        onChange={(e) => updateEmailAccountMutation.mutate({ id: acc.id, data: { private_ai_mode: e.target.value } })}
                                                        className="text-xs p-1 border border-gray-200 dark:border-gray-700 rounded-lg dark:bg-slate-800 dark:text-white">
                                                        <option value="excluded">IA: no leer</option>
                                                        <option value="generalized">IA: leer y generalizar</option>
                                                    </select>
                                                )}
                                                <button
                                                    onClick={() => setEditingSignature(
                                                        editingSignature?.id === acc.id ? null : { id: acc.id, html: acc.signature_html || '' }
                                                    )}
                                                    className="text-xs font-bold text-gray-500 hover:text-blue-600 flex items-center gap-1"
                                                >
                                                    <span className="material-symbols-outlined text-sm">signature</span>
                                                    {acc.signature_html ? 'Editar firma' : 'Agregar firma'}
                                                </button>
                                            </div>

                                            {editingSignature?.id === acc.id && (
                                                <div className="pl-14 space-y-2">
                                                    <p className="text-xs text-gray-500">
                                                        Se agrega al pie de los correos que envíes desde esta cuenta, incluidos los que responda la IA sola.
                                                    </p>
                                                    <RichTextEditor
                                                        value={editingSignature.html}
                                                        onChange={(html) => setEditingSignature({ ...editingSignature, html })}
                                                        placeholder="Nombre, cargo, teléfono, web..."
                                                    />
                                                    <div className="flex gap-2">
                                                        <button
                                                            onClick={() => updateEmailAccountMutation.mutate(
                                                                { id: acc.id, data: { signature_html: editingSignature.html } },
                                                                { onSuccess: () => { setEditingSignature(null); setMessage({ type: 'success', text: 'Firma guardada.' }); } }
                                                            )}
                                                            disabled={updateEmailAccountMutation.isPending}
                                                            className="py-1.5 px-4 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold rounded-lg disabled:opacity-50"
                                                        >
                                                            Guardar firma
                                                        </button>
                                                        <button onClick={() => setEditingSignature(null)} className="py-1.5 px-4 text-xs font-bold text-gray-500">
                                                            Cancelar
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    </section>
                    )}

                    {/* Meta Integrations */}
                    {isFeatureEnabled('settings', 'instagram') && (
                    <section className="bg-white dark:bg-surface-dark rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm overflow-hidden animate-in fade-in duration-500">
                        <div className="p-6 border-b border-gray-100 dark:border-gray-800 flex justify-between items-center bg-pink-50/50 dark:bg-pink-900/10">
                            <div>
                                <h3 className="text-lg font-bold text-text-main dark:text-white flex items-center gap-2">
                                    <span className="material-symbols-outlined text-pink-500">hub</span>
                                    Instagram
                                </h3>
                                <p className="text-sm text-text-secondary">Conecta tu Instagram Business para recibir DMs y comentarios.</p>
                            </div>
                        </div>

                        <div className="p-6 space-y-6">
                            <button
                                onClick={() => getOAuthUrlMutation.mutate('instagram')}
                                disabled={getOAuthUrlMutation.isPending}
                                className="w-full sm:w-auto flex items-center justify-center gap-3 py-3 px-6 border border-gray-200 dark:border-gray-700 rounded-xl hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors"
                            >
                                <img src="https://www.svgrepo.com/show/452229/instagram-1.svg" alt="Instagram" className="w-6 h-6" />
                                <span className="text-sm font-bold dark:text-white">Conectar con Instagram</span>
                            </button>

                            <div className="space-y-3">
                                <h4 className="text-sm font-bold text-gray-800 dark:text-gray-200 border-b border-gray-100 dark:border-gray-800 pb-2">Páginas Conectadas</h4>
                                {igLoading ? (
                                    <div className="text-sm text-gray-500">Cargando...</div>
                                ) : instagramAccounts.length === 0 ? (
                                    <div className="text-sm text-gray-400">No tienes cuentas de Instagram conectadas.</div>
                                ) : (
                                    (instagramAccounts || []).map((acc) => (
                                        <div key={acc.id} className="flex items-center justify-between p-4 bg-white dark:bg-slate-800/20 border border-gray-200 dark:border-gray-700/50 rounded-xl">
                                            <div className="flex items-center gap-4">
                                                <div className="w-10 h-10 rounded-full bg-pink-50 dark:bg-pink-900/30 flex items-center justify-center">
                                                    <span className="material-symbols-outlined text-pink-500">photo_camera</span>
                                                </div>
                                                <div>
                                                    <p className="text-sm font-bold dark:text-white">@{acc.instagram_username}</p>
                                                    <p className="text-xs text-gray-500">Página: {acc.page_name}</p>
                                                </div>
                                            </div>
                                            <button onClick={() => {
                                                if(window.confirm('¿Desconectar esta cuenta?')) disconnectInstagramMutation.mutate(acc.id);
                                            }} className="p-2 hover:bg-red-50 text-red-500 rounded-lg">
                                                <span className="material-symbols-outlined">link_off</span>
                                            </button>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    </section>
                    )}

                    {/* Data Management */}
                    <section className="bg-white dark:bg-surface-dark rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm overflow-hidden animate-in fade-in duration-500">
                        <div className="p-6 border-b border-gray-100 dark:border-gray-800 flex justify-between items-center bg-gray-50/50 dark:bg-gray-900/30">
                            <div>
                                <h3 className="text-lg font-bold text-text-main dark:text-white flex items-center gap-2">
                                    <span className="material-symbols-outlined text-gray-500">database</span>
                                    Gestión de Datos
                                </h3>
                                <p className="text-sm text-text-secondary">Mantenimiento de la base de datos de contactos.</p>
                            </div>
                        </div>
                        <div className="p-6">
                            <div className="flex items-start justify-between gap-4 p-5 rounded-xl border border-dashed border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/20">
                                <div className="flex items-start gap-3">
                                    <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center flex-shrink-0">
                                        <span className="material-symbols-outlined text-amber-600 dark:text-amber-400">archive</span>
                                    </div>
                                    <div>
                                        <p className="font-semibold text-gray-900 dark:text-white text-sm">Contactos Archivados (Unificados)</p>
                                        <p className="text-xs text-gray-500 mt-0.5 max-w-sm">
                                            Cuando unificas dos contactos, el duplicado queda archivado para mantener el historial. Puedes purgarlos permanentemente aquí.
                                        </p>
                                        <div className="mt-2">
                                            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold ${
                                                mergedCount > 0
                                                    ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                                                    : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400'
                                            }`}>
                                                <span className="material-symbols-outlined text-[12px]">folder_open</span>
                                                {mergedCount} contacto{mergedCount !== 1 ? 's' : ''} archivado{mergedCount !== 1 ? 's' : ''}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <button
                                    onClick={() => setShowPurgeConfirm(true)}
                                    disabled={mergedCount === 0 || purgeMutation.isPending}
                                    className="flex-shrink-0 flex items-center gap-2 px-4 py-2 rounded-lg border border-red-200 dark:border-red-800/50 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-40 disabled:cursor-not-allowed text-sm font-semibold transition-all"
                                >
                                    <span className="material-symbols-outlined text-base">delete_sweep</span>
                                    Purgar
                                </button>
                            </div>
                        </div>
                    </section>

                </div>
            </div>

            {/* Purge Confirmation Modal */}
            {showPurgeConfirm && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowPurgeConfirm(false)} />
                    <div className="relative z-10 bg-white dark:bg-[#1a100c] rounded-2xl shadow-2xl border border-gray-100 dark:border-gray-800 w-full max-w-sm overflow-hidden">
                        <div className="p-6 text-center">
                            <div className="w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mx-auto mb-4">
                                <span className="material-symbols-outlined text-red-600 dark:text-red-400 text-3xl">delete_forever</span>
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">¿Purgar contactos archivados?</h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
                                Esta acción eliminará permanentemente los <strong>{mergedCount} contacto{mergedCount !== 1 ? 's' : ''}</strong> archivados. <strong>No se puede deshacer.</strong>
                            </p>
                        </div>
                        <div className="flex border-t border-gray-100 dark:border-gray-800">
                            <button
                                onClick={() => setShowPurgeConfirm(false)}
                                className="flex-1 px-4 py-4 text-sm font-semibold text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={() => purgeMutation.mutate()}
                                disabled={purgeMutation.isPending}
                                className="flex-1 px-4 py-4 text-sm font-bold text-white bg-red-600 hover:bg-red-700 transition-colors flex items-center justify-center gap-2"
                            >
                                {purgeMutation.isPending && <span className="material-symbols-outlined animate-spin text-sm">sync</span>}
                                Purgar
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
